import React, { useState } from 'react';
import { Mail, MapPin, Phone, ArrowRight, CheckCircle2, Instagram, Facebook, Clock } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
  };

  const services = [
    'Custom Website Development',
    'Landing Page Development',
    'E-Commerce Website',
    'UI/UX Design',
    'Website Optimization',
    'Deployment & Maintenance',
    'Not sure yet',
  ];

  return (
    <div data-testid="contact-page" className="page-transition">
      {/* Hero Section */}
      <section className="pt-40 pb-20 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111] to-[#050505]" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <span className="overline">Get In Touch</span>
            <h1 className="heading-hero text-[#F5F5F7] mt-4 mb-6">
              Let's Build Your
              <br />
              <span className="text-gradient-gold">Website</span>
            </h1>
            <p className="body-large">
              Ready to launch a high-performance website? Book a free consultation and let's 
              discuss how we can bring your vision to life.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding bg-[#050505]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-16">
            {/* Form */}
            <div className="lg:col-span-3">
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-[#F5F5F7] text-sm font-medium mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        data-testid="contact-input-name"
                        value={formData.name}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-[#F5F5F7] text-sm font-medium mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        data-testid="contact-input-email"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="company" className="block text-[#F5F5F7] text-sm font-medium mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        data-testid="contact-input-company"
                        value={formData.company}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Your Company"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-[#F5F5F7] text-sm font-medium mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        data-testid="contact-input-phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-[#F5F5F7] text-sm font-medium mb-2">
                      Service Interested In
                    </label>
                    <select
                      id="service"
                      name="service"
                      data-testid="contact-select-service"
                      value={formData.service}
                      onChange={handleChange}
                      className="form-input appearance-none cursor-pointer"
                    >
                      <option value="">Select a service...</option>
                      {services.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-[#F5F5F7] text-sm font-medium mb-2">
                      Tell Us About Your Project *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      data-testid="contact-input-message"
                      value={formData.message}
                      onChange={handleChange}
                      className="form-input resize-none"
                      placeholder="What kind of website do you need? Any specific features or timeline?"
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    className="btn-primary w-full md:w-auto"
                  >
                    Send Message
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </form>
              ) : (
                <div 
                  data-testid="contact-success-message"
                  className="bg-[#111111] border border-[#D4AF37]/30 p-10 text-center"
                >
                  <CheckCircle2 className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
                  <h3 className="text-2xl font-semibold text-[#F5F5F7] mb-4">
                    Message Received!
                  </h3>
                  <p className="text-[#A1A1AA] mb-6">
                    Thank you for reaching out. We'll review your inquiry and get back to you 
                    within 24-48 business hours.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="text-[#D4AF37] font-medium hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              )}
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2">
              <div className="bg-[#111111] border border-white/10 p-8 mb-8">
                <h3 className="text-xl font-semibold text-[#F5F5F7] mb-6">
                  Contact Information
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4 text-[#A1A1AA]">
                    <Mail className="w-5 h-5 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-[#71717A] text-sm mb-1">Email</p>
                      <a 
                        href="mailto:hello@eleven11-studios.com"
                        data-testid="contact-email-primary"
                        className="hover:text-[#D4AF37] transition-colors block"
                      >
                        hello@eleven11-studios.com
                      </a>
                      <a 
                        href="mailto:studioeleven380@gmail.com"
                        data-testid="contact-email-link"
                        className="hover:text-[#D4AF37] transition-colors block text-sm mt-1"
                      >
                        studioeleven380@gmail.com
                      </a>
                    </div>
                  </div>
                  <a 
                    href="tel:+917081111181"
                    data-testid="contact-phone-link"
                    className="flex items-start gap-4 text-[#A1A1AA] hover:text-[#D4AF37] transition-colors"
                  >
                    <Phone className="w-5 h-5 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-[#71717A] text-sm mb-1">Phone</p>
                      <p>+91-7081111181</p>
                    </div>
                  </a>
                  <div className="flex items-start gap-4 text-[#A1A1AA]">
                    <Clock className="w-5 h-5 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-[#71717A] text-sm mb-1">Business Hours</p>
                      <p>Mon–Fri, 10 AM – 6 PM IST</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 text-[#A1A1AA]">
                    <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-[#71717A] text-sm mb-1">Office</p>
                      <p className="text-sm">22-23, Block S, DLF Phase 3,<br />Gurugram, Haryana, India 122002</p>
                      <p className="text-[#71717A] text-xs mt-2">Serving clients in USA & Canada</p>
                    </div>
                  </div>
                </div>

                {/* Social Media Links */}
                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-[#71717A] text-sm mb-4">Follow Us</p>
                  <div className="flex items-center gap-4">
                    <a
                      href="https://www.instagram.com/eleven11_studios_/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-instagram"
                      className="w-10 h-10 bg-white/5 border border-white/10 flex items-center justify-center text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-all"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                    <span
                      data-testid="contact-facebook"
                      className="w-10 h-10 bg-white/5 border border-white/10 flex items-center justify-center text-[#71717A] cursor-not-allowed"
                      title="Coming soon"
                    >
                      <Facebook className="w-5 h-5" />
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-[#0a0a0a] border border-white/10 p-8">
                <h3 className="text-xl font-semibold text-[#F5F5F7] mb-4">
                  What Happens Next?
                </h3>
                <ol className="space-y-4">
                  {[
                    'We review your inquiry within 24-48 hours',
                    'Schedule a free consultation call',
                    'Receive a custom proposal for your website',
                    'Begin building your website',
                  ].map((step, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-[#D4AF37]/20 text-[#D4AF37] text-sm font-bold flex items-center justify-center flex-shrink-0">
                        {index + 1}
                      </span>
                      <span className="text-[#A1A1AA] text-sm">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-[#0a0a0a]">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="overline">FAQ</span>
            <h2 className="heading-section text-[#F5F5F7] mt-4">
              Common Questions
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                q: 'How long does a typical website project take?',
                a: 'A typical website project takes 4-8 weeks depending on scope and complexity. Simple landing pages can be completed in 2-3 weeks, while larger e-commerce sites may take 8-12 weeks.',
              },
              {
                q: 'What\'s your pricing structure?',
                a: 'We offer project-based pricing tailored to your specific needs. After our consultation call, we\'ll provide a detailed proposal with transparent pricing based on your requirements.',
              },
              {
                q: 'Do you work with businesses outside the USA & Canada?',
                a: 'Our primary focus is businesses in the USA and Canada, but we do consider international clients on a case-by-case basis. Reach out and let\'s discuss.',
              },
              {
                q: 'What makes Eleven11 different from other web design studios?',
                a: 'We focus on craftsmanship over volume. Every website is custom-built from scratch, with meticulous attention to performance, user experience, and conversion optimization. We keep our client roster intentionally small to ensure premium attention.',
              },
            ].map((faq, index) => (
              <div 
                key={index}
                data-testid={`faq-item-${index}`}
                className="premium-card p-6"
              >
                <h4 className="text-[#F5F5F7] font-medium mb-3">{faq.q}</h4>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
